// Groove JSON API
import { toQueryString } from 'util/params'
import { createQueue, queueify } from 'util/queue'
import { getTabId } from 'util/tabId'
import { getVersion } from 'util/version'
import { v4 as uuidV4 } from 'uuid'

const reportToBugsnag = (method, err, path, requestId) => {
  const escapedPath = path
    .split('?')
    .shift()
    .replace(/\/\d+/g, '/?')
  const requestType = [method, escapedPath].join(' ')
  if (!bugsnagClient || !bugsnagClient.notify) return
  bugsnagClient.notify(new Error(`API ${requestType} call failed`), {
    severity: 'error',
    beforeSend: report => {
      // eslint-disable-next-line no-param-reassign
      report.groupingHash = requestType
    },
    metaData: {
      request: {
        requestId,
      },
      errorDetails: {
        errorClass: err.name,
        errorMessage: err.message,
        stack: err.stack,
        requestType,
        path,
      },
    },
  })
}

export class GrooveAPI {
  constructor({ apiUrl, concurrency, withCredentials = true }) {
    this.apiUrl = apiUrl
    this.queueId = createQueue(concurrency)
    this.withCredentials = withCredentials
    this.get = queueify(this.queueId, this.get.bind(this))
    this.send = queueify(this.queueId, this.send.bind(this))
  }

  get(token, path, params, headers) {
    return new Promise((resolve, reject) => {
      const query = toQueryString(params)
      const parts = [this.apiUrl, '/', path]

      if (query !== '') parts.push(`?${query}`)

      const url = parts.join('')
      const request = new XMLHttpRequest()
      const requestId = uuidV4()

      request.open('GET', url, true)
      request.setRequestHeader('Content-Type', 'application/json')
      request.setRequestHeader('Authorization', `Bearer ${token}`)
      request.withCredentials = this.withCredentials
      request.setRequestHeader('X-Client-Tab-Id', getTabId())
      request.setRequestHeader('X-Client-App-Version', getVersion())
      request.setRequestHeader('X-Request-Id', requestId)
      if (headers) {
        Object.keys(headers).forEach(key => {
          request.setRequestHeader(key, headers[key])
        })
      }
      request.onload = () => {
        const text = request.responseText
        let json = null
        try {
          json = JSON.parse(text)
          resolve({
            code: request.status,
            body: text,
            json,
          })
        } catch (err) {
          reject({
            code: request.status,
            body: text,
            json,
          })
        }
      }
      request.onerror = err => {
        console.error(err) // eslint-disable-line

        let shouldReport = true
        if (url && url.match('metrics.json')) shouldReport = false
        if (shouldReport) reportToBugsnag('GET', err, path, requestId)

        const betterError = new TypeError('Network request failed')
        reject(betterError)
      }
      request.send(null)
    })
  }

  send(method, token, path, params, body = null, headers) {
    return new Promise((resolve, reject) => {
      const query = toQueryString(params)
      const parts = [this.apiUrl, '/', path]

      if (query !== '') parts.push(`?${query}`)

      const url = parts.join('')
      const request = new XMLHttpRequest()
      const requestId = uuidV4()

      request.open(method, url, true)
      request.setRequestHeader('Content-Type', 'application/json')
      request.setRequestHeader('Authorization', `Bearer ${token}`)
      request.withCredentials = this.withCredentials
      request.setRequestHeader('X-Client-Tab-Id', getTabId())
      request.setRequestHeader('X-Client-App-Version', getVersion())
      request.setRequestHeader('X-Request-Id', requestId)
      if (headers) {
        Object.keys(headers).forEach(key => {
          request.setRequestHeader(key, headers[key])
        })
      }

      request.onload = () => {
        let json
        try {
          json = JSON.parse(request.responseText)
        } catch (err) {
          json = null
        }
        resolve({
          code: request.status,
          body: request.responseText,
          json,
        })
      }
      request.onerror = err => {
        console.error(err) // eslint-disable-line

        let shouldReport = true
        if (url && url.match('metrics.json')) shouldReport = false
        if (shouldReport) reportToBugsnag(method, err, path, requestId)

        const betterError = new TypeError('Network request failed')
        reject(betterError)
      }
      request.send(body)
    })
  }

  put(token, path, params, body, headers) {
    return this.send('PUT', token, path, params, body, headers)
  }

  post(token, path, params, body, headers) {
    return this.send('POST', token, path, params, body, headers)
  }

  graphql(token, path, query, variables) {
    return this.post(
      token,
      path,
      null,
      JSON.stringify({ query, variables })
    ).then(({ json }) => json)
  }

  delete(token, path, params) {
    return this.send('DELETE', token, path, params)
  }
}

export default (apiUrl, apiConcurrency) =>
  new GrooveAPI({ apiUrl, concurrency: apiConcurrency })
